import React from "react";
import { SECCIONES, GRUPOS, NIVEL, anchorId, progresoObligatorias } from "./secciones";

const Check = ({ color = "#fff" }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12l6 6L20 6" />
  </svg>
);
const Candado = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

function marcador(sec, av, esActual) {
  if (esActual) return { cls: "cev__mk--cur", icon: <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} /> };
  const estado = av?.estado;
  if (sec.nivel === NIVEL.OBLIGATORIA) {
    return estado === "completa" ? { cls: "cev__mk--ok", icon: <Check /> } : { cls: "cev__mk--miss", icon: null };
  }
  if (sec.nivel === NIVEL.RECOMENDADA) {
    return estado === "completa" ? { cls: "cev__mk--ok", icon: <Check /> } : { cls: "cev__mk--sug", icon: null };
  }
  return estado === "completa"
    ? { cls: "cev__mk--done", icon: <Check color="var(--brand)" /> }
    : { cls: "cev__mk--opt", icon: null };
}

function ItemRail({ s, avance, seccionActual, onNavegar, compacto }) {
  const av = avance[s.id];
  const esActual = seccionActual === s.id;
  const mk = marcador(s, av, esActual);
  const falta = ["vacia", "parcial"].includes(av?.estado) && s.nivel !== NIVEL.OPCIONAL;
  // En la franja compacta el "—" de las opcionales no aporta y sí ocupa.
  const detalle = compacto && av?.detalle === "—" ? null : av?.detalle;
  return (
    <a
      href={`#${anchorId(s.id)}`}
      className={"cev__ri " + (esActual ? "cev__ri--cur" : "")}
      aria-current={esActual ? "true" : undefined}
      title={compacto ? s.titulo : undefined}
      onClick={(e) => {
        e.preventDefault();
        onNavegar(s.id);
      }}
    >
      <span className={"cev__mk " + mk.cls}>{mk.icon}</span>
      <span className="lab" style={falta ? { color: "var(--warning-text)" } : undefined}>
        {compacto ? s.corto || s.titulo : s.titulo}
      </span>
      {detalle && <span className={"cnt " + (falta ? "cnt--miss" : "")}>{detalle}</span>}
    </a>
  );
}

export default function RailAvance({ avance, seccionActual, onNavegar, variant = "lateral" }) {
  const prog = progresoObligatorias(avance);
  const faltaObl = SECCIONES.filter(
    (s) => s.nivel === NIVEL.OBLIGATORIA && avance[s.id]?.estado !== "completa"
  ).map((s) => s.titulo.toLowerCase());

  const progBar = (
    <div className="cev__prog" role="progressbar" aria-valuemin={0} aria-valuemax={prog.total} aria-valuenow={prog.hechas}>
      <span style={{ width: `${Math.round(prog.ratio * 100)}%` }} />
    </div>
  );

  // ---- horizontal: fila superior a todo el ancho (útil antes de iniciar) ----
  if (variant === "horizontal") {
    return (
      <nav className="cev__railh" aria-label="Avance de la consulta">
        <div className="cev__railh-prog" title={faltaObl.length ? `Falta ${faltaObl.join(", ")}` : "Obligatorias completas"}>
          <span className="cev__railh-prog-n">
            <b>{prog.hechas}/{prog.total}</b> oblig.
          </span>
          {progBar}
        </div>
        {/* En orden clínico (el del submenú original), NO agrupadas por nivel:
            la franja tiene que leerse igual que se apila la página, o el salto
            desorienta. El nivel lo dice el marcador de cada pastilla. */}
        <div className="cev__railh-scroll">
          {SECCIONES.map((s) => (
            <ItemRail key={s.id} s={s} avance={avance} seccionActual={seccionActual} onNavegar={onNavegar} compacto />
          ))}
        </div>
      </nav>
    );
  }

  // ---- lateral: rail fijo a la izquierda ----
  // OJO: esta variante agrupa por nivel, así que su orden NO coincide con el
  // de la página (que es el clínico, ver SECCIONES). Hoy no se usa; si se
  // recupera, hay que decidir entre agrupar o respetar el orden de la hoja.
  return (
    <aside className="cev__rail" aria-label="Avance de la consulta">
      <div className="cev__rlbl">Avance de la consulta</div>
      {progBar}
      <div className="cev__pnote">
        <b>{prog.hechas} de {prog.total}</b> obligatorias
        {faltaObl.length > 0 && ` · falta ${faltaObl.join(", ")}`}
      </div>

      {GRUPOS.map((g) => {
        const items = SECCIONES.filter((s) => s.nivel === g.nivel);
        return (
          <div key={g.nivel}>
            <div className="cev__rsep" />
            <div
              className={
                "cev__rlbl " +
                (g.nivel === NIVEL.OBLIGATORIA ? "cev__rlbl--ob" : g.nivel === NIVEL.RECOMENDADA ? "cev__rlbl--re" : "")
              }
              style={{ paddingBottom: 5 }}
            >
              {g.etiqueta}{" "}
              {g.sufijo && (
                <span className="cev__lock">
                  <Candado />
                  {g.sufijo}
                </span>
              )}
            </div>
            <ul className="cev__rlist">
              {items.map((s) => (
                <li key={s.id}>
                  <ItemRail s={s} avance={avance} seccionActual={seccionActual} onNavegar={onNavegar} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <div className="cev__rfoot">
        La barra de arriba mide solo las obligatorias. Las recomendadas se avisan al
        finalizar, pero el médico decide.
      </div>
    </aside>
  );
}
